import React, { useState, useEffect } from 'react';
import Header from './common/Header';
import { Link, useLocation } from 'react-router-dom';
import { retreive_failures } from '../api';

// This component renders a side-by-side comparison for one tuple of data.
const SideBySideComparison = ({ expectedData, generatedData, orderType }) => {
  // Define the fields you want to compare.
  const fields = ['productId', 'colorId', 'styleName', 'quantity', 'colorName', 'notes'];

  // The scorer reports three kinds of failure: a matched pair that scored below 1.0, an
  // expected line with no generated counterpart, and a generated line with no expected
  // counterpart. Only the last case -- neither side present -- has nothing to show. The
  // one-sided cases still render, with N/A down the missing column, because a group that
  // was missed entirely is exactly what QA needs to see.
  if (!expectedData && !generatedData) return null;

  return (
    <table>
      <thead>
        <tr>
          <th colSpan={3} style={styles.th}>
            <strong>{orderType}</strong>
            {!generatedData && <span style={styles.missing}> — no generated line matched</span>}
            {!expectedData && <span style={styles.missing}> — no expected line matched</span>}
          </th>
        </tr>
        <tr>
          <th>Name</th>
          <th>Generated</th>
          <th>Expected</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => {
          const generatedValue = generatedData?.[field];
          const expectedValue = expectedData?.[field];
          const isDifferent = generatedValue !== expectedValue;
          return (
            <tr key={field} className={field === 'productId' ? 'compare-builder-tr' : ''}>
              <td style={styles.td}>{field}</td>
              <td style={{ ...styles.td, ...(isDifferent ? styles.deleted : {}) }}>
                {generatedValue || 'N/A'}
              </td>
              <td style={{ ...styles.td, ...(isDifferent ? styles.added : {}) }}>
                {expectedValue || 'N/A'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const CompareBuilderRule = () => {
  // Retrieve the builder values passed via the Link's state.
  const location = useLocation();
  const { builder_name, timestamp } = location.state || {};

  // Local state to store the fetched failures data.
  const [failuresData, setFailuresData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // On mount, call the API using the passed builder_name and timestamp.
  useEffect(() => {
    if (builder_name && timestamp) {
      retreive_failures(timestamp, builder_name)
        .then((data) => {
          const parsedFailures = JSON.parse(data.failed_cases);
          setFailuresData(parsedFailures)
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching failures:', err);
          setError(err);
          setLoading(false);
        });
    }
  }, [builder_name, timestamp]);

  if (loading) return <div>Loading failures...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div>
      <svg style={{ display: 'none' }}>
        <symbol id="arrow-back" width="12" height="24" viewBox="0 0 12 24">
          <rect width="12" height="24" fill="white" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.71992 5.84794C8.7642 5.80255 8.80406 5.75495 8.83837 5.70182C8.8738 5.64979 8.90258 5.59444 8.92693 5.53687C8.95129 5.47931 8.969 5.41842 8.98118 5.35754C8.99336 5.29554 9 5.23355 9 5.17045C9 5.13945 8.99779 5.10735 8.99557 5.07636C8.99225 5.04536 8.98782 5.01436 8.98118 4.98337C8.97565 4.95237 8.9679 4.92248 8.95904 4.89259C8.94908 4.8627 8.93911 4.83281 8.92694 4.80403C8.91476 4.77525 8.90148 4.74647 8.88709 4.71879C8.87159 4.69112 8.85609 4.66455 8.83838 4.63909C8.82066 4.61252 8.80295 4.58706 8.78303 4.56271C8.7631 4.53835 8.74207 4.5151 8.71993 4.49296C8.69778 4.47083 8.67454 4.44979 8.65018 4.42986C8.62583 4.40994 8.60037 4.39223 8.5738 4.37451C8.54834 4.3568 8.52177 4.3413 8.49409 4.3258C8.46642 4.31141 8.43764 4.29813 8.40886 4.28595C8.38007 4.27378 8.35019 4.26381 8.3203 4.25385C8.29041 4.24499 8.26052 4.23724 8.22952 4.23171C8.19852 4.22507 8.16753 4.22064 8.13653 4.21732C8.10554 4.2151 8.07343 4.21289 8.04244 4.21289C7.97934 4.21289 7.91735 4.21953 7.85535 4.23171C7.79447 4.24389 7.73469 4.2616 7.67602 4.28595C7.61845 4.31031 7.5631 4.33909 7.51107 4.37452C7.45794 4.40883 7.41033 4.44869 7.36495 4.49297L0.280074 11.5778C0.257934 11.6 0.236902 11.6232 0.216973 11.6476C0.197047 11.6719 0.179336 11.6974 0.161624 11.724C0.143912 11.7494 0.128412 11.776 0.112913 11.8037C0.0985219 11.8313 0.0852386 11.8601 0.0730628 11.8889C0.0608859 11.9177 0.0509222 11.9476 0.0409594 11.9775C0.0321033 12.0074 0.0243542 12.0373 0.0188192 12.0682C0.012177 12.0992 0.00774915 12.1302 0.00442805 12.1612C0.00221388 12.1922 0 12.2243 0 12.2553C0 12.2863 0.00221388 12.3184 0.00442805 12.3494C0.00774915 12.3804 0.012177 12.4114 0.0188192 12.4424C0.0243542 12.4734 0.0321033 12.5033 0.0409594 12.5332C0.0509225 12.5631 0.0608861 12.593 0.0730628 12.6217C0.08524 12.6505 0.0985234 12.6793 0.112913 12.707C0.128411 12.7347 0.143912 12.7612 0.161624 12.7867C0.179336 12.8133 0.197048 12.8387 0.216973 12.8631C0.236899 12.8874 0.257932 12.9107 0.280074 12.9328L7.36495 20.0177C7.41034 20.062 7.45794 20.1018 7.51107 20.1361C7.5631 20.1716 7.61845 20.2003 7.67602 20.2247C7.73469 20.2491 7.79447 20.2668 7.85535 20.2789C7.91735 20.2911 7.97934 20.2978 8.04244 20.2978C8.07344 20.2978 8.10554 20.2956 8.13653 20.2933C8.16753 20.29 8.19853 20.2856 8.22952 20.2789C8.26052 20.2734 8.29041 20.2657 8.3203 20.2568C8.35019 20.2468 8.38007 20.2369 8.40886 20.2247C8.43764 20.2125 8.46642 20.1992 8.49409 20.1849C8.52177 20.1694 8.54834 20.1539 8.5738 20.1361C8.60037 20.1184 8.62583 20.1007 8.65018 20.0808C8.67454 20.0609 8.69779 20.0398 8.71993 20.0177C8.74206 19.9956 8.7631 19.9723 8.78303 19.948C8.80295 19.9236 8.82066 19.8981 8.83838 19.8716C8.85609 19.8461 8.87159 19.8195 8.88709 19.7919C8.90148 19.7642 8.91476 19.7354 8.92694 19.7066C8.93911 19.6778 8.94908 19.648 8.95904 19.6181C8.9679 19.5882 8.97565 19.5583 8.98118 19.5273C8.98782 19.4963 8.99225 19.4653 8.99557 19.4343C8.99779 19.4033 9 19.3712 9 19.3402C9 19.2771 8.99336 19.2151 8.98118 19.1531C8.969 19.0922 8.95129 19.0325 8.92693 18.9738C8.90258 18.9162 8.8738 18.8609 8.83837 18.8088C8.80406 18.7557 8.7642 18.7081 8.71992 18.6627L2.31151 12.2554L8.71992 5.84794Z"
            fill="#6D727A"
          />
        </symbol>
      </svg>
      <Header />
      <main>
        <div className="pof2frms-container">
          <div className="pof2frms-head">
            <h1>
              <Link to="/builder-rules">
                <svg width="12" height="24">
                  <use href="#arrow-back" />
                </svg>
              </Link>
              {builder_name} - {timestamp}
            </h1>
          </div>
          <div className="po2frms-table compare-builder-rule">
            {failuresData &&
              Object.keys(failuresData).map((batchName) => {
                const batch = failuresData[batchName];
                return (
                  <div key={batchName} className="batch-section">
                    <h2 style={styles.heading_style}>{batchName}</h2>
                    {Object.keys(batch).map((orderType) => {
                      const comparisons = batch[orderType];
                      return (
                        <div key={orderType} className="order-type-section">
                          {/* <h3>{orderType}</h3> */}
                          {comparisons.map((tuple, index) => (
                            <SideBySideComparison
                              key={index}
                              // OrderScorer.score_all_orders(expected, generated) builds these
                              // tuples as (expected_line, test_line), so index 0 is expected and
                              // index 1 is the generated line -- not the other way round.
                              expectedData={tuple[0]}
                              generatedData={tuple[1]}
                              orderType={orderType}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        </div>
      </main>
    </div>
  );
};

const styles = {
  th: {
    textAlign: 'center'
  },
  td: {
    padding: '8px',
    border: '1px solid #ccc'
  },
  deleted: {
    color: 'var(--error)'
  },
  added: {
    color: 'var(--success)'
  },
  missing: {
    fontWeight: 'normal',
    fontStyle: 'italic',
    color: 'var(--error)'
  },
  heading_style: {
    padding: '20px',
    margin: '0px',
    background: 'var(--purple)',
    color: '#fff',
    textAlign: 'center'
  }
};

export default CompareBuilderRule;
